<template>
  <header>
    <AlertErrorMsg
      ref="alert"
      :title="errorMessage"
      class="page-alert-error"
    />
    <div class="header-registered">
      <div class="header-title">
        <div id="header_nav_btn" class="title-return">
          <button :disabled="!canGoBack" @click="onGoBack">
            <GreyArrowLeft18 />
          </button>
          <button :disabled="!canGoForward" @click="onGoForward">
            <GreyArrowRight18 />
          </button>
          <h3>
            <span v-text="title || headTitle" />
          </h3>
        </div>
      </div>
      <nav v-if="loggingIn" class="header-nav">
        <ul class="navigation">
          <li
            v-if="isExportInProgress"
            class="nav-item export-loader"
          >
            <CircularLoader make-small />
          </li>
          <li
            v-if="isExportSuccessful"
            ref="successIconContainer"
            class="nav-item success-icon"
            @click="showExportSuccessTooltip"
          >
            <SuccessIcon24 ref="successIcon" />
            <WhiteTooltip
              ref="exportSuccessTooltip"
              :target-ref="successIconRef || {}"
              side="bottom"
              header-text="Export is completed!"
              @click="navigateToExportPage"
            >
              <div class="tooltip-content">
                <span>{{ exportCaseName || 'Case' }} has been successfully exported.
                  You can find the download link in the export history table
                </span>
                <span class="export-date">{{ formattedExportDate }}</span>
              </div>
            </WhiteTooltip>
          </li>
          <li
            id="navigation_header_btn"
            class="nav-item"
          >
            <HeaderNotificationsTemplate
              :notifications="notifications"
              @changedStatus="changeStatus"
            />
          </li>
          <li
            v-if="photoProfile"
            id="navigation_header_user_image"
            class="nav-item"
            @click.stop="$refs.userDropdown.toggleActive()"
          >
            <div class="holder-img">
              <img
                class="user-image"
                :src="photoProfile"
                alt=""
                onerror="this.src='https://cdn4.iconfinder.com/data/icons/avatars-xmas-giveaway/128/girl_female_woman_avatar-512.png'"
              >
            </div>
          </li>
          <li
            v-else
            id="profile_header_btn"
            class="nav-item"
            @click.stop="$refs.userDropdown.toggleActive()"
          >
            <div class="user-no-image">
              <span>{{ userInitials }}</span>
            </div>
          </li>
        </ul>
        <HeaderDropdown ref="userDropdown" @logout="openLogoutDialog" />
      </nav>
    </div>
    <logout-popup
      ref="logoutDialog"
      @onConfirm="logoutUser"
    />
  </header>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import LocalStorageService from '../../services/LocalStorageService';
import HeaderNotificationsTemplate from './HeaderNotificationsTemplate';
import HeaderDropdown from './HeaderDropdown';
import LogoutPopup from '@/components/popups/LogoutPopup.vue';
import CircularLoader from '@/components/loader/CircularLoader.vue';
import SuccessIcon24 from '@/components/icons/SuccessIcon24.vue';
import GreyArrowLeft18 from '@/components/icons/GreyArrowLeft18.vue';
import GreyArrowRight18 from '@/components/icons/GreyArrowRight18.vue';
import AlertErrorMsg from '@/components/profile/AlertErrorMsg.vue';
import WhiteTooltip from '@/components/common/WhiteTooltip.vue';
import parseDate from '@/mixins/parseDate';

const localStorageService = LocalStorageService.getService();

export default {
  name: 'HeaderRegistered',
  components: {
    WhiteTooltip,
    AlertErrorMsg,
    GreyArrowRight18,
    GreyArrowLeft18,
    LogoutPopup,
    HeaderDropdown,
    HeaderNotificationsTemplate,
    CircularLoader,
    SuccessIcon24,
  },
  mixins: [parseDate],
  props: {
    title: {
      type: String,
      default: () => null
    },
    subTitle: {
      type: String,
      default: '',
    },
    withoutRouteName: {
      type: Boolean,
      default: () => false
    },
  },
  data() {
    return {
      loggingIn: true,
      errorMessage: '',
    };
  },
  computed: {
    ...mapGetters('routerHistory', {
      canGoBack: 'canGoBack',
      canGoForward: 'canGoForward',
    }),
    ...mapGetters('appState', {
      isExportInProgress: 'getIsExportInProgress',
      isExportSuccessful: 'getIsExportSuccessful',
      exportCaseName: 'getExportCaseName',
      exportedCaseId: 'getExportedCaseId',
      exportDate: 'getExportDate'
    }),
    successIconRef() {
      return this.$refs.successIconContainer;
    },
    formattedExportDate() {
      if (!this.exportDate) return '';

      const date = new Date(this.exportDate);

      return this.parseDate(date);
    },
    headTitle() {
      return this.withoutRouteName
        ? this.subTitle : this.subTitle
          ? this.$route.name + ' ' + this.subTitle : this.$route.name;
    },
    photoProfile() {
      return this.$store.state.profile.user.photoProfile;
    },
    notifications() {
      return this.$store.state.notifications.notificationsList;
    },
    userInitials() {
      if (this.$store.state.profile.user.firstName && this.$store.state.profile.user.lastName) {
        return this.$store.state.profile.user.firstName.substring(0, 1).toUpperCase()
          + this.$store.state.profile.user.lastName.substring(0, 1).toUpperCase();
      }
      return '';
    },
  },
  watch: {
    isExportSuccessful(newValue) {
      if (newValue) {
        this.$nextTick(() => {
          this.showExportSuccessTooltip();
        });
      }
    }
  },
  mounted() {
    if (localStorageService.getAccessTokenLogin() !== null) {
      this.$store.commit('authentication/loginSuccess', localStorageService.getAccessTokenLogin);
    }

    this.getNotificationAll({}).then(() => {
      this.getNotificationCount({});
    });

    this.$store.dispatch('profile/getUser');

  },
  methods: {
    ...mapActions({
      getNotificationCount: 'notifications/getNotificationCount',
      getNotificationAll: 'notifications/getNotificationAll'
    }),
    ...mapActions('routerHistory', ['goBack', 'goForward']),
    onGoBack() {
      this.goBack();
    },
    onGoForward() {
      this.goForward();
    },
    changeStatus() {
      this.getNotificationAll({}).then(() => {
        this.getNotificationCount({});
      });
    },
    openLogoutDialog() {
      this.$refs.logoutDialog.openPopups();
    },
    logoutUser() {
      localStorageService.clearAllToLocalStorageData();
      this.$store.commit('authentication/logout');
      this.$router.push({name: 'LoginInAccount'});
    },
    showExportSuccessTooltip() {
      if (this.$refs.exportSuccessTooltip && this.$refs.successIconContainer) {
        this.$refs.exportSuccessTooltip.targetElement = this.$refs.successIconContainer;
        this.$refs.exportSuccessTooltip.showTooltip();
      }
    },
    navigateToExportPage() {
      if (this.exportedCaseId) {
        this.$router.push({
          name: 'Export',
          params: {slug: this.exportedCaseId}
        });
      } else {
        this.$router.push({name: 'Export'});
      }
    },
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

ul {
  padding: 0;
  list-style: none;
}

header {
  padding: 10px 16px;
  border-bottom: 1px solid var(--input-placeholder-value-color);
}

.header-title {
  width: 100%;
  display: flex;
  align-items: center;
}

.title-return {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 0 15px;
}

.title-return button {
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
}

.title-return button svg {
  margin-right: 10px;
  cursor: pointer;
}

.title-return button:disabled svg {
  opacity: 0.4;
  cursor: default;
}

.title-return h3 {
  margin: 0 15px;
}

.header-registered {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

header h3 {
  font-size: 14px;
  font-weight: 600;
  line-height: 17px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;

  margin: 0 30px 0 0;
  text-overflow: ellipsis;
  display: inline-block;
  white-space: nowrap;
  color: var(--secondary-black-color);
}

.header-nav {
  position: relative;
}

.navigation {
  display: flex;
  align-items: center;
  gap: 15px;
  margin: 0;
}

.nav-item {
  cursor: pointer;
  position: relative;
}

.holder-img {
  max-width: 24px;
  max-height: 24px;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
}

.user-image {
  width: 24px;
  -o-object-fit: cover;
  object-fit: cover;
  border-radius: 50%;
  height: 24px;
}

.notification-circle {
  position: absolute;
  top: -6px;
  right: 16px;
}

.notification-circle svg circle {
  fill: var(--accent-color);
  transition: all .2s;
}

.notification-circle.notification-empty svg circle {
  fill: #DDE3E3;
  transition: all .2s;
}

.nav-item:last-child {
  padding-right: 0;
}

.user-no-image {
  background: #1E325C;
  border-radius: 50%;
  min-width: 24px;
  min-height: 24px;
  margin: 0 0 5px 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: var(--white-color);
  font-size: 11.2px;
  font-weight: 400;
  line-height: 11.2px;
  text-align: center;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
}

.user-profile-public-page__wrapper .title-return svg {
  display: inline-block;
}

.export-loader {
  display: flex;
  align-items: center;
}

.export-loader .spinner-container {
  margin: 0;
}

.export-loader .loader {
  width: 24px;
  height: 24px;
}

.export-loader .loader::before {
  border-width: 5px;
}

.success-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.to-export-page {
  color: var(--accent-color);
  cursor: pointer;
  text-decoration: underline;
}

.tooltip-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.export-date {
  font-weight: 400;
  font-size: 12px;
  color: var(--light-gray);
  margin-top: 5px;
}

</style>
